import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { Card, Button } from '../components/common';
import { createCheckoutSession, getRecommendedGateway } from '../services/paymentService';
import useRazorpay from '../hooks/useRazorpay';

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const { openCheckout: openRazorpayCheckout, loading: razorpayLoading } = useRazorpay();

  // Fetch recommended gateway based on location
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await getRecommendedGateway();
        if (response.success) {
          setPriceInfo(response.data);
        }
      } catch (err) {
        // Default to USD pricing if geo detection fails
        setPriceInfo({
          gateway: 'stripe',
          currency: 'USD',
          price: 19,
          priceDisplay: '$19',
          period: 'month'
        });
      }
    };
    fetchPricing();
  }, []);

  // Show status messages if redirected from payment
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      navigate('/register?redirect=/pricing');
      return;
    }

    if (user?.plan === 'pro') {
      toast.success('You are already on the Pro plan!');
      return;
    }

    setLoading(true);
    const { track } = await import('../utils/analytics').then(m => ({ track: m.analytics.track }));
    track('upgrade_clicked');
    try {
      const response = await createCheckoutSession();

      if (!response.success) {
        throw new Error(response.error || 'Failed to create checkout');
      }

      const checkoutData = response.data;

      if (checkoutData.type === 'redirect') {
        // Stripe flow - redirect to hosted checkout
        window.location.href = checkoutData.checkoutUrl;
      } else if (checkoutData.type === 'modal') {
        // Razorpay flow - open modal checkout
        setLoading(false);
        openRazorpayCheckout(
          checkoutData,
          async (result) => {
            // Success - refresh user data and navigate
            toast.success(result.message || 'Payment successful! Welcome to Pro!');
            if (refreshUser) {
              await refreshUser();
            }
            navigate('/settings?payment=success');
          },
          (error) => {
            // Failure
            if (error.message !== 'Payment cancelled') {
              toast.error(error.message || 'Payment failed');
            }
          }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to start checkout');
      setLoading(false);
    }
  };

  // Get display price based on geo detection
  const getProPrice = () => {
    if (!priceInfo) return { display: '₹999', period: 'per year', equivalent: null };
    return {
      display: priceInfo.priceDisplay,
      period: priceInfo.periodLabel || 'per year',
      equivalent: priceInfo.equivalent
    };
  };

  const proPrice = getProPrice();
  const isInternational = priceInfo?.isInternational;

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for trying out Kwikly',
      features: [
        { text: '3 job captures per week', included: true },
        { text: '2 resume tailors per month', included: true },
        { text: 'Cover letter generation', included: true },
        { text: 'PDF export with watermark', included: true },
        { text: 'Basic ATS scoring', included: true },
        { text: 'Unlimited resume storage', included: false },
        { text: 'Priority support', included: false },
        { text: 'Watermark-free exports', included: false }
      ],
      cta: user?.plan === 'free' ? 'Current Plan' : 'Get Started',
      current: user?.plan === 'free',
      onSelect: () => navigate('/register')
    },
    {
      name: 'Pro',
      price: proPrice.display,
      period: proPrice.period,
      description: 'For serious job seekers',
      features: [
        { text: 'Unlimited job captures', included: true },
        { text: 'Unlimited resume tailoring', included: true },
        { text: 'Cover letter generation', included: true },
        { text: 'Watermark-free PDF exports', included: true },
        { text: 'Advanced ATS scoring', included: true },
        { text: 'Unlimited resume storage', included: true },
        { text: 'Priority support', included: true },
        { text: 'Early access to new features', included: true }
      ],
      cta: user?.plan === 'pro' ? 'Current Plan' : 'Upgrade to Pro',
      current: user?.plan === 'pro',
      popular: true,
      onSelect: handleUpgrade
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Start free and upgrade when you need more. No hidden fees, cancel anytime.
          </p>
        </div>

        {/* Notice for international users */}
        {isInternational && (
          <div className="mb-8 max-w-2xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800 text-sm">
                <span className="font-medium">International cards accepted!</span>
                {' '}We accept Visa, Mastercard, and American Express from anywhere in the world.
                {proPrice.equivalent && (
                  <span className="block mt-1 text-green-600">
                    You&apos;ll be charged {proPrice.equivalent} (approximately {proPrice.display})
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative ${plan.popular ? 'border-2 border-primary-500' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary-500 text-white text-sm font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6 pt-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h2>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 ml-1">/{plan.period}</span>
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {feature.included ? (
                      <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={plan.onSelect}
                loading={plan.name === 'Pro' && (loading || razorpayLoading)}
                disabled={plan.current}
                variant={plan.popular ? 'primary' : 'secondary'}
                className="w-full"
              >
                {plan.cta}
              </Button>
            </Card>
          ))}
        </div>

        {/* FAQ or additional info */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Frequently Asked Questions
          </h3>
          <div className="max-w-2xl mx-auto space-y-6 text-left">
            <div>
              <h4 className="font-medium text-gray-900 mb-1">
                What happens when I hit my free tier limits?
              </h4>
              <p className="text-gray-600">
                You&apos;ll see a message explaining you&apos;ve reached your limit. Your limits reset weekly (for job captures) or monthly (for tailoring). You can upgrade anytime for unlimited access.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">
                Can I cancel anytime?
              </h4>
              <p className="text-gray-600">
                Yes! You can cancel your Pro subscription at any time. You&apos;ll keep Pro access until the end of your billing period, then automatically switch to the free tier.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">
                What payment methods do you accept?
              </h4>
              <p className="text-gray-600">
                {isInternational
                  ? 'We accept all major international credit and debit cards including Visa, Mastercard, and American Express.'
                  : 'We accept UPI, all major credit/debit cards, and net banking.'}
              </p>
            </div>
          </div>
        </div>

        {/* Back to app link */}
        {isAuthenticated && (
          <div className="mt-12 text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              ← Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
